// The white panel that holds a screen, and its header row. Parts build their
// screens inside these, so every screen has the same frame and the same place
// for the title and the actions.
//
// Embedded in a host that draws the frame (shell/outside) there is neither the
// corner button nor a header row: the title goes out as data.
//
// On a phone with the Dock (frame.phone) the corner has no button: the Dock
// at the bottom opens the menu. Nor is there a header row: a Dock floats at the
// top with the actions (`PhoneTopDock`), the title under it.
//
// The button that opens the sidebar (compact) sits at the same spot on every
// screen, with or without a header row: 11 px from the panel's left edge and
// level with the header row's buttons, or right of the window controls when
// they cover the corner. Screens without a header row (settings) do not give
// it a row of its own: it floats at the corner and their body moves right of
// it, so the title stays at the top (`useCornerClear`).
import { useNavigate } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { type CSSProperties, type ReactNode, useRef } from "react";
import { useMeApp } from "../app/context";
import { ICON_BUTTON_SIZE, IconButton } from "../ui/button";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { cornerStyle, type ShellFrame, useShellFrame } from "./frame";
import { actionsOf } from "./header-data";
import { ListAddButton } from "./list-add";
import {
  type ShellHeaderInput,
  useShellEmbedded,
  useShellHeader,
  useShellTitle,
} from "./outside/outside";
import { PhoneTopDock } from "./phone";
import { SideViewBack, SideViewControls, useSideView } from "./side-view";
import { useTitleBarClear } from "./title-bar";

/** The open button's distance from the panel's left edge (the old web's). */
const CORNER_BUTTON_LEFT = 11;
/** The header rows' own left padding (`px-4`). */
const ROW_PADDING = 16;
/** Between the open button and what follows it (`gap-2`). */
const AFTER_BUTTON = 8;

/** Opens the sidebar drawer; first in a header row, at the corner's spot. */
export function OpenSidebarButton() {
  const frame = useShellFrame();
  return (
    <IconButton
      label={m.shell_open_sidebar()}
      onClick={frame.openDrawer}
      // The row's padding is 16; the button stands 11 from the edge (unless
      // window controls push the row right of them).
      className={frame.cornerInset > 0 ? "" : "-ml-[5px]"}
    >
      <Icon kind="panel" size={15} />
    </IconButton>
  );
}

/** Where the open button's right edge is, from the panel's left edge. */
const cornerButtonEnd = (frame: ShellFrame) =>
  (frame.cornerInset > 0
    ? Math.max(frame.cornerInset, ROW_PADDING)
    : CORNER_BUTTON_LEFT) + ICON_BUTTON_SIZE;

/**
 * For a body under the floating open button (no header row): the left padding
 * that puts its title right of the button, when compact. `padding` is the
 * body's own left padding, `inset` how far inside it the title starts.
 */
export function useCornerClear(
  padding: number,
  inset = 0,
): CSSProperties | undefined {
  const frame = useShellFrame();
  const embedded = useShellEmbedded();
  // A phone opens the sidebar from the bottom, not this corner; embedded, the
  // host opens its own.
  if (!frame.compact || frame.phone || embedded) return undefined;
  return {
    paddingLeft: Math.max(
      padding,
      cornerButtonEnd(frame) + AFTER_BUTTON - inset,
    ),
  };
}

export function PanelHeader({
  header,
  title = header?.title,
  titleEnd,
  actions = header ? actionsOf(header, false) : undefined,
}: {
  /**
   * The header as the outside's data (shell/outside): drawn here (its title,
   * unless `title` draws it richer, and its buttons) and sent to a frame drawn
   * outside.
   */
  header?: ShellHeaderInput;
  title?: ReactNode;
  /**
   * Right after the title, kept whole (`PersonalMark`): outside the title's
   * fading end, which would also clip its tooltip.
   */
  titleEnd?: ReactNode;
  actions?: ReactNode;
}) {
  const frame = useShellFrame();
  const side = useSideView() !== null;
  const embedded = useShellEmbedded() && !side;
  const row = useRef<HTMLElement>(null);
  const clear = useTitleBarClear(row);
  useShellHeader(side ? null : (header ?? null));
  // A screen's own header wins over this plain title.
  useShellTitle(
    embedded && !header && typeof title === "string" ? title : null,
  );
  // Embedded, the host draws the header, except at a phone's width: there the
  // phone's top Dock keeps its room and the title (the host draws its buttons).
  if (embedded && !frame.phone) return null;
  // A phone has a Dock at the top instead, and the title under it.
  if (frame.phone)
    return (
      <PhoneTopDock
        back={!side}
        actions={actions}
        title={
          titleEnd ? (
            <>
              {title}
              {titleEnd}
            </>
          ) : (
            title
          )
        }
        hostBack={header?.back === true}
      />
    );
  return (
    <header
      ref={row}
      // A container: the side view's controls give way by the row's own width.
      className="@container flex h-11 flex-none items-center gap-2 border-b border-border-soft px-4"
      // A side view is not at the panel's top-left corner.
      style={{ ...(side ? undefined : cornerStyle(frame)), ...clear }}
    >
      {!side && frame.compact && <OpenSidebarButton />}
      <SideViewBack />
      {titleEnd ? (
        <div className="flex min-w-0 flex-1 items-center">
          <h1 className="fade-end min-w-0 text-[13.5px] font-normal text-fg">
            {title}
          </h1>
          {titleEnd}
        </div>
      ) : (
        <h1 className="fade-end min-w-0 flex-1 text-[13.5px] font-normal text-fg">
          {title}
        </h1>
      )}
      {actions}
      <SideViewControls />
    </header>
  );
}

/**
 * For screens without a header row (settings): opens the sidebar drawer when
 * compact. It floats at the corner where the header rows put it (same height,
 * same spot), so moving between screens does not move it, and takes no row:
 * the body beside it moves right instead (`PanelBody`'s `cornerClear`,
 * `useCornerClear`). Its parent must be positioned (the panel is).
 */
export function DrawerButton() {
  const frame = useShellFrame();
  const embedded = useShellEmbedded();
  if (!frame.compact || frame.phone || embedded) return null;
  return (
    <div
      className="absolute top-0 left-0 z-10 flex h-11 items-center px-4"
      style={cornerStyle(frame)}
    >
      <OpenSidebarButton />
    </div>
  );
}

/** The body's own left padding (`px-6`). */
const BODY_PADDING = 24;

/**
 * A screen with only a body (settings, empty states). `cornerClear`: it sits
 * under the floating `DrawerButton`, so its column starts right of it when
 * compact.
 */
export function PanelBody({
  children,
  cornerClear = false,
}: {
  children: ReactNode;
  cornerClear?: boolean;
}) {
  const clear = useCornerClear(BODY_PADDING);
  return (
    <div
      className="panel-scroll min-h-0 flex-1 select-text overflow-y-auto px-6 pt-7 pb-10"
      style={cornerClear ? clear : undefined}
    >
      {children}
    </div>
  );
}

/**
 * A list's header: its name (and the label it is narrowed to, faint), and the
 * button that makes a new one, as data (`PanelHeader`'s `header`).
 */
export function ListHeader({
  title,
  label,
  add,
}: {
  title: string;
  /** The label the list is narrowed to. */
  label?: string | null;
  add?: { label: string; to: string; params?: Record<string, string> };
}) {
  const navigate = useNavigate();
  const header: ShellHeaderInput = {
    title: label ? `${title} · ${label}` : title,
    actions: add
      ? [
          {
            id: "add",
            label: add.label,
            icon: "plus",
            onPress: () =>
              void navigate({ to: add.to, params: add.params } as never),
            render: () => (
              <ListAddButton
                label={add.label}
                to={add.to}
                params={add.params}
              />
            ),
          },
        ]
      : [],
  };
  return (
    <PanelHeader
      header={header}
      title={
        <>
          {title}
          {label && <span className="text-faint"> · {label}</span>}
        </>
      }
      titleEnd={<PersonalMark />}
    />
  );
}

/**
 * After a Personal list's title: one faint icon of where it is kept (a cloud,
 * a PC for the Vault; a lock when the host does not say), whose tooltip (a
 * pointer at rest or a tap) also says that only the person sees what is in it.
 */
export function PersonalMark() {
  const place = useMeApp().extensions.personalPlace;
  const phone = useShellFrame().phone;
  const text =
    place === "cloud"
      ? m.shell_personal_in_cloud()
      : place === "vault"
        ? m.shell_personal_in_vault()
        : m.shell_personal_only();
  return (
    // On a phone the title is at the top: above it is the status bar.
    <Tooltip text={text} tap side={phone ? "below" : "above"}>
      <span
        role="img"
        aria-label={text}
        // On a phone, beside the 21 px title: at 15, then 20, the cloud read
        // as too small, and it goes with the title's foot (the person,
        // 2026-11-03). Its box's bottom sits on the baseline, which stood the
        // cloud over the letters: 5 px lower, its foot is on the baseline. A
        // finger needs room around it.
        className={`inline-flex flex-none text-faint ${phone ? "-my-[6px] -mr-[6px] p-[6px] align-[-5px]" : "ml-[6px]"}`}
      >
        <Icon
          kind={place === "cloud" ? "cloud" : place === "vault" ? "pc" : "lock"}
          size={phone ? 24 : 12}
        />
      </span>
    </Tooltip>
  );
}
